// src/services/quoteService.js
import apiClient from "./api";

/**
 * Helper to map Ant Design (or similar) table parameters to API query parameters.
 * @param {object} params - Parameters from table (pagination, sorter, filters, search).
 * @returns {object} Query parameters for the API.
 */
const mapTableParamsToApi = (params = {}) => {
  const apiParams = {};
  if (params.pagination) {
    apiParams.page = params.pagination.current;
    apiParams.pageSize = params.pagination.pageSize;
  }
  if (params.filters) {
    // Example: If filtering by status directly from AntD filters
    if (params.filters.status && params.filters.status.length > 0) {
      apiParams.status = params.filters.status[0]; // Assuming single status filter
    }
    // Add mappings for other filters if needed
  }
  if (params.sorter && params.sorter.field) {
    apiParams.sortField = params.sorter.field;
    // Map AntD 'ascend'/'descend' to backend expected values if needed
    apiParams.sortOrder = params.sorter.order === "ascend" ? "asc" : "desc"; // Adjust if backend expects 'asc'/'desc'
  } else {
    // Default sort if needed (e.g., by creation date descending)
    // apiParams.sortField = 'createdAt';
    // apiParams.sortOrder = 'desc';
  }
  if (params.search) {
    // Add direct search param if passed
    apiParams.search = params.search;
  }
  return apiParams;
};

const API = "api";

/**
 * Fetches quotes from the backend with pagination, sorting, and filtering.
 * @param {object} params - Raw parameters from the UI component (e.g., AntD table onChange).
 * @returns {Promise<{data: Array<object>, total: number, page: number, pageSize: number, totalPages: number}>} Paginated quote data.
 * @throws {Error} If fetching fails.
 */
export const getQuotes = async (params = {}) => {
  try {
    const apiParams = mapTableParamsToApi(params);
    const response = await apiClient.get(`${API}/quotes`, {
      params: apiParams,
    });
    // Ensure the backend response has the expected structure
    return response.data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch quotes.";
    console.error("getQuotes service error:", message);
    throw new Error(message);
  }
};

/**
 * Adds a new quote request.
 * @param {object} quoteData - Data for the new quote { name, email, phone?, projectType, message }.
 * @returns {Promise<object>} The newly created quote object from the backend.
 * @throws {Error} If creation fails.
 */
export const addQuote = async (quoteData) => {
  try {
    const response = await apiClient.post(`${API}/quotes`, quoteData);
    return response.data;
  } catch (error) {
    const message =
      error.response?.data?.message || error.message || "Failed to add quote.";
    console.error("addQuote service error:", message);
    throw new Error(message);
  }
};

/**
 * Updates an existing quote.
 * @param {string} quoteId - The ID of the quote to update.
 * @param {object} updatedData - The fields to update.
 * @returns {Promise<object>} The updated quote object from the backend.
 * @throws {Error} If update fails.
 */
export const updateQuote = async (quoteId, updatedData) => {
  try {
    // Ensure ID is not accidentally sent in the body
    const dataToSend = { ...updatedData };
    delete dataToSend._id;
    delete dataToSend.id; // Just in case

    const response = await apiClient.put(
      `${API}/quotes/${quoteId}`,
      dataToSend
    );
    return response.data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to update quote.";
    console.error("updateQuote service error:", message);
    throw new Error(message);
  }
};

/**
 * Deletes a quote.
 * @param {string} quoteId - The ID of the quote to delete.
 * @returns {Promise<{success: boolean, id: string}>} Confirmation object from the backend.
 * @throws {Error} If deletion fails.
 */
export const deleteQuote = async (quoteId) => {
  try {
    const response = await apiClient.delete(`${API}/quotes/${quoteId}`);
    return response.data; // Expects { success: true, id: quoteId, message: '...' }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to delete quote.";
    console.error("deleteQuote service error:", message);
    throw new Error(message);
  }
};

/**
 * Updates the status of a specific quote.
 * @param {string} quoteId - The ID of the quote.
 * @param {string} status - The new status value.
 * @returns {Promise<object>} The updated quote object from the backend.
 * @throws {Error} If status update fails.
 */
export const updateQuoteStatus = async (quoteId, status) => {
  try {
    const response = await apiClient.patch(`${API}/quotes/${quoteId}/status`, {
      status,
    }); // Send status in body
    return response.data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to update quote status.";
    console.error("updateQuoteStatus service error:", message);
    throw new Error(message);
  }
};
